import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CompanyLogo from '../common/CompanyLogo';
import { useNavigate } from 'react-router-dom';
import { 
  FileText, 
  Search, 
  Calendar, 
  ChevronLeft, 
  ChevronRight, 
  ArrowUpRight, 
  RefreshCw,
  FileCheck2
} from 'lucide-react';
import { fetchDrhpRegistry } from '../../store/slices/drhpSlice';
import { selectGmpBoardRows } from '../../store/slices/gmpSlice';
import { DOC_TYPES, DRHP_STATUS, buildDrhpRows, filterDrhpRows } from '../../utils/drhpTracker';
import { formatFilingDate } from '../../utils/upcomingIpos';

const DATA_MAX_AGE_MS = 5 * 60 * 1000;
const BOARD_TABS = { ALL: 'all', MAINBOARD: 'mainboard', SME: 'sme' };

export const FilingsDirectory = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // GET /api/core/ipos/drhp-tracker: board, regulatory status and filing dates.
  // The grey market board (already loaded by the ticker) fills in exchanges.
  const records = useSelector((state) => state.drhp.records);
  const detailsById = useSelector((state) => state.drhp.detailsById);
  const status = useSelector((state) => state.drhp.status);
  const error = useSelector((state) => state.drhp.error);
  const gmpRows = useSelector(selectGmpBoardRows);

  // Local state for filters and client search
  const [searchQuery, setSearchQuery] = useState('');
  const [boardFilter, setBoardFilter] = useState('ALL'); // 'ALL' | 'MAINBOARD' | 'SME'
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  useEffect(() => {
    dispatch(fetchDrhpRegistry({ maxAgeMs: DATA_MAX_AGE_MS }));
  }, [dispatch]);

  const filings = useMemo(
    () => buildDrhpRows({ records, detailsById, gmpRows }),
    [records, detailsById, gmpRows]
  );

  // The tracker returns every filing at once, so filters and paging are local
  const filteredIpos = useMemo(
    () => filterDrhpRows(filings, { tab: BOARD_TABS[boardFilter], query: searchQuery }),
    [filings, boardFilter, searchQuery]
  );

  const isLoading = !filings.length && (status === 'idle' || status === 'loading');
  const hasFailed = !filings.length && status === 'failed';

  const totalPages = Math.max(1, Math.ceil(filteredIpos.length / pageSize));
  const page = Math.min(currentPage, totalPages);
  const pageRows = filteredIpos.slice((page - 1) * pageSize, page * pageSize);

  // Back to page one whenever the result set changes
  useEffect(() => {
    setCurrentPage(1);
  }, [boardFilter, searchQuery]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== page) {
      setCurrentPage(newPage);
      // Scroll gently to table header
      const tableElem = document.getElementById('drhp-filings-section');
      if (tableElem) {
        tableElem.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  const handleRetry = () => {
    dispatch(fetchDrhpRegistry());
  };

  const navigateToDetails = (detailPath) => navigate(detailPath);

  // Generate visible pagination buttons
  const renderPaginationNumbers = () => {
    const buttons = [];
    const maxButtons = 5;
    let startPage = Math.max(1, page - 2);
    let endPage = Math.min(totalPages, startPage + maxButtons - 1);

    if (endPage - startPage < maxButtons - 1) {
      startPage = Math.max(1, endPage - maxButtons + 1);
    }

    for (let p = startPage; p <= endPage; p++) {
      buttons.push(
        <button
          key={p}
          onClick={() => handlePageChange(p)}
          className={`w-8 h-8 rounded-lg text-xs font-semibold transition-all ${
            page === p
              ? 'bg-[#008080] text-white shadow-xs'
              : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
          }`}
        >
          {p}
        </button>
      );
    }
    return buttons;
  };

  return (
    <section
      id="drhp-filings-section"
      className="py-12 sm:py-16 bg-white border-t border-slate-200/80"
    >
      <div className="w-full max-w-[1680px] mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-28">
        
        {/* Section Header */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-6 sm:mb-8">
          <div>
            <div className="flex items-center gap-2.5">
              <span className="w-2.5 h-2.5 rounded-full bg-[#008080] animate-pulse" />
              <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
                DRHP Filings & IPO Pipeline
              </h2>
              {filings.length > 0 && (
                <span className="hidden sm:inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-teal-50 text-[#008080] border border-teal-200">
                  {filings.length} Filings Documented
                </span>
              )}
            </div>
            <p className="text-slate-500 text-xs sm:text-sm mt-1 max-w-2xl">
              Official draft red herring prospectuses (DRHP) filed with SEBI and stock exchanges. Track prospective issues before they hit the live market.
            </p>
          </div>

          {/* Quick Filter Tabs & Search Bar */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            {/* Board Filter Pills */}
            <div className="inline-flex p-1 bg-slate-100 rounded-xl border border-slate-200/80 self-start sm:self-auto">
              <button
                onClick={() => setBoardFilter('ALL')}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  boardFilter === 'ALL'
                    ? 'bg-white text-slate-900 shadow-xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                All Filings
              </button>
              <button
                onClick={() => setBoardFilter('MAINBOARD')}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  boardFilter === 'MAINBOARD'
                    ? 'bg-white text-slate-900 shadow-xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Mainboard
              </button>
              <button
                onClick={() => setBoardFilter('SME')}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  boardFilter === 'SME'
                    ? 'bg-white text-slate-900 shadow-xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                SME Issues
              </button>
            </div>

            {/* Quick Search Box */}
            <div className="relative min-w-[220px]">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="text"
                placeholder="Search company or filing..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-1.5 bg-white border border-slate-200 rounded-xl text-xs text-slate-800 placeholder-slate-400 focus:outline-hidden focus:border-[#008080] focus:ring-1 focus:ring-[#008080] transition-all"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-slate-400 hover:text-slate-600"
                >
                  ✕
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Data Table Card Container */}
        <div className="bg-white rounded-2xl border border-slate-200/90 shadow-xs overflow-hidden">
          <div className="overflow-x-auto scrollbar-none">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/90 border-b border-slate-200/80 text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                  <th className="py-3.5 px-4 sm:px-6 min-w-[240px]">Company Name</th>
                  <th className="py-3.5 px-4 min-w-[140px]">DRHP Date</th>
                  <th className="py-3.5 px-4 text-center">Exchange</th>
                  <th className="py-3.5 px-4 text-center">Issue Type</th>
                  <th className="py-3.5 px-4 hidden md:table-cell min-w-[200px]">Regulatory Status</th>
                  <th className="py-3.5 px-4 sm:px-6 text-right">Action</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100 text-xs sm:text-sm">
                {/* Loading State Skeleton */}
                {isLoading ? (
                  Array.from({ length: 8 }).map((_, idx) => (
                    <tr key={`skel-${idx}`} className="animate-pulse">
                      <td className="py-4 px-4 sm:px-6">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-slate-200" />
                          <div className="space-y-1.5 flex-1">
                            <div className="w-44 h-4 bg-slate-200 rounded" />
                            <div className="w-24 h-3 bg-slate-100 rounded" />
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="w-24 h-4 bg-slate-200 rounded" />
                      </td>
                      <td className="py-4 px-4 text-center">
                        <div className="w-12 h-5 bg-slate-200 rounded-full mx-auto" />
                      </td>
                      <td className="py-4 px-4 text-center">
                        <div className="w-16 h-5 bg-slate-200 rounded-full mx-auto" />
                      </td>
                      <td className="py-4 px-4 hidden md:table-cell">
                        <div className="w-36 h-4 bg-slate-200 rounded" />
                      </td>
                      <td className="py-4 px-4 sm:px-6 text-right">
                        <div className="w-20 h-7 bg-slate-200 rounded-lg ml-auto" />
                      </td>
                    </tr>
                  ))
                ) : hasFailed ? (
                  /* Error State */
                  <tr>
                    <td colSpan={6} className="py-12 text-center">
                      <div className="max-w-md mx-auto space-y-3">
                        <div className="w-12 h-12 rounded-full bg-rose-50 text-rose-600 flex items-center justify-center mx-auto">
                          <RefreshCw className="w-5 h-5" />
                        </div>
                        <p className="text-slate-700 font-medium text-sm">
                          {error || 'The DRHP tracker is temporarily unavailable.'}
                        </p>
                        <button
                          onClick={handleRetry}
                          className="px-4 py-1.5 bg-[#008080] text-white text-xs font-semibold rounded-lg hover:bg-[#006666] transition-colors"
                        >
                          Retry Loading Filings
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : filteredIpos.length === 0 ? (
                  /* Empty State */
                  <tr>
                    <td colSpan={6} className="py-12 text-center">
                      <div className="max-w-md mx-auto space-y-2 text-slate-500">
                        <FileText className="w-8 h-8 mx-auto text-slate-300" />
                        <p className="font-semibold text-slate-700 text-sm">
                          No filings match your criteria
                        </p>
                        <p className="text-xs">
                          Try searching for another company or clearing your board filter.
                        </p>
                        {searchQuery && (
                          <button
                            onClick={() => setSearchQuery('')}
                            className="mt-2 text-xs text-[#008080] font-semibold hover:underline"
                          >
                            Clear Search Query
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  /* Data Rows */
                  pageRows.map((row) => {
                    const isSme = row.board === 'sme';
                    const boardLabel = isSme ? 'SME IPO' : row.board === 'mainboard' ? 'Mainboard' : null;
                    const filedOn = row.drhpFiledOn ? formatFilingDate(row.drhpFiledOn) : 'In process';
                    const DOC_LINE = { DRHP: 'Draft filing', RHP: 'Red herring filed', Prospectus: 'Final prospectus' };
                    const subLine = row.note || (row.cin ? `CIN: ${row.cin}` : DOC_LINE[row.docType]);

                    return (
                      <tr
                        key={row.id}
                        className="hover:bg-teal-50/30 transition-colors group"
                      >
                        {/* Company Column */}
                        <td className="py-3.5 px-4 sm:px-6">
                          <div className="flex items-center gap-3">
                            <CompanyLogo company={row.company} logoUrl={row.logoUrl} size="sm" maxInitials={1} />
                            <div className="min-w-0">
                              <div
                                onClick={() => navigateToDetails(row.detailPath)}
                                className="font-bold text-slate-900 hover:text-[#008080] transition-colors truncate cursor-pointer"
                                title={row.company}
                              >
                                {row.company}
                              </div>
                              <div className="text-[11px] text-slate-400 truncate" title={subLine}>
                                {subLine}
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* DRHP Date Column */}
                        <td className="py-3.5 px-4 whitespace-nowrap">
                          <div className="flex items-center gap-1.5 text-slate-700 font-medium text-xs">
                            <Calendar className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                            <span>{filedOn}</span>
                          </div>
                        </td>

                        {/* Exchange Column */}
                        <td className="py-3.5 px-4 text-center whitespace-nowrap">
                          {row.exchanges.length ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-bold bg-slate-100 text-slate-700 border border-slate-200">
                              {row.exchanges.join(' / ')}
                            </span>
                          ) : (
                            <span className="text-slate-300">—</span>
                          )}
                        </td>

                        {/* Issue Type Column */}
                        <td className="py-3.5 px-4 text-center whitespace-nowrap">
                          {boardLabel ? (
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-bold ${
                                isSme
                                  ? 'bg-amber-50 text-amber-700 border border-amber-200'
                                  : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                              }`}
                            >
                              {boardLabel}
                            </span>
                          ) : (
                            <span className="text-slate-300">—</span>
                          )}
                        </td>

                        {/* Regulatory Status Column: the tracker's own status, and the document it's at */}
                        <td className="py-3.5 px-4 hidden md:table-cell whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <span className={`inline-flex px-1.5 py-0.5 rounded border text-[10px] font-extrabold ${DOC_TYPES[row.docType]}`}>
                              {row.docType}
                            </span>
                            <span className={`inline-flex px-2 py-0.5 rounded-md border text-[11px] font-bold ${DRHP_STATUS[row.status].badge}`}>
                              {DRHP_STATUS[row.status].label}
                            </span>
                            {row.analysisReady && (
                              <FileCheck2 className="w-3.5 h-3.5 text-[#008080] flex-shrink-0" title="DRHP analysis published" />
                            )}
                          </div>
                        </td>

                        {/* Action Column */}
                        <td className="py-3.5 px-4 sm:px-6 text-right whitespace-nowrap">
                          <button
                            onClick={() => navigateToDetails(row.detailPath)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold text-[#008080] bg-teal-50 hover:bg-[#008080] hover:text-white border border-teal-200 transition-all shadow-2xs"
                          >
                            <span>View Details</span>
                            <ArrowUpRight className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Footer */}
          <div className="bg-slate-50/80 px-4 sm:px-6 py-4 border-t border-slate-200/80 flex items-center justify-center">
            <div className="flex items-center gap-1.5 sm:gap-2">
              <button
                onClick={() => handlePageChange(page - 1)}
                disabled={page <= 1 || isLoading}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-2xs"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
                <span>Previous</span>
              </button>

              <div className="flex items-center gap-1">
                {renderPaginationNumbers()}
              </div>

              <button
                onClick={() => handlePageChange(page + 1)}
                disabled={page >= totalPages || isLoading}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-2xs"
              >
                <span>Next</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default FilingsDirectory;
